import React from "react";
import PanelShell from "../shared/PanelShell";
import { fmtNumber } from "../shared/format";

// WORKLOAD — Tier 4 (V6 §E.11, Addendum 1 §5b.3). engaged vs
// abandoned-before-speaking, shown SEPARATELY and never as a stacked bar: they are
// the comparison, and stacking them invites the wrong denominator (their sum).
//
// handled_without_a_person is the pre-computed count of people the agent handled
// without a transfer — displayed, never derived. V6 §E.11 warned against deriving a
// rate from the total, and this is the number that respects that warning.
//
// self_resolution_rate is computed by the backend (against engaged only — a caller
// who hung up during the greeting never had a conversation to resolve), so it is
// rendered, never derived. It now differs sharply by channel (74.2 Phone / 98.3
// Chat) and getAgentValue is called per-modality, so the scalar here is already the
// page's own value — NEVER a combined figure. Zero engaged → the rate is null, and
// the line is omitted rather than rendered as "0%", which reads as total failure.
//
// A thin conversation-specific panel rather than weather-intelligence/StatPair: that
// component is welded to weather's promise_vs_delivery / booking_horizon_split field
// names and labels (user ruling 2026-09-01 — the catalogue forms are feature-welded;
// reuse the conversation panels or build thin).
//
// Each figure drills through fn_drill using the descriptor the backend ships beside
// it — drill_engaged, drill_abandoned (never_spoke), drill_handled — and is a button
// ONLY when its descriptor is present. The list has to come from the backend: the
// frontend never filters conversations to build one, so a figure without a
// descriptor stays a plain figure rather than a click that opens a mismatched list.

const HANDLED_LABEL = "handled without a person";

const Stat = ({ value, label, onClick, testId }) => {
  const inner = (
    <>
      <span className="gi-number text-gray-900 dark:text-gray-100 tabular-nums transition-colors group-hover:text-[#2099DF] dark:group-hover:text-[#32B1FF]">{fmtNumber(value)}</span>
      <span className="gi-label text-gray-500 dark:text-gray-400 mt-0.5 transition-colors group-hover:text-[#2099DF] dark:group-hover:text-[#32B1FF] group-hover:underline underline-offset-4">{label}</span>
    </>
  );
  if (!onClick) {
    return <div data-testid={testId} className="flex flex-col">{inner}</div>;
  }
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="group flex flex-col text-left cursor-pointer rounded-md min-h-[44px] px-1 -mx-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
    >
      {inner}
    </button>
  );
};

/** Capitalise the first letter so a figure's label doubles as its drawer title. */
const asTitle = (label) => label.charAt(0).toUpperCase() + label.slice(1);

const Workload = ({ panelKey = "voice_workload", say, workload, onDrill, loading = false }) => {
  const wl = workload || {};
  const engaged = wl.engaged;
  const abandoned = wl.abandoned_before_speaking;
  const handled = wl.handled_without_a_person;
  const rate = wl.self_resolution_rate;
  // §E.11 — an all-zero workload HIDES. "0 engaged / 0 abandoned" is not a measured
  // state worth a card: it reads as "0% resolved", total failure, when it actually
  // means nothing happened in the range. So present-but-zero counts as no data, the
  // same as absent (a null guard alone rendered the misleading zeros). Any non-zero
  // count in EITHER figure is real and lights the card — including a positive
  // abandoned with zero engaged.
  const hasData = Number(engaged) > 0 || Number(abandoned) > 0;

  // Chat visitors type, they do not speak — the abandoned figure reads "abandoned
  // before engaging" on Chat, "abandoned before speaking" on Phone (Markup #17).
  // Label only; the value and drill are unchanged.
  const abandonedLabel = String(panelKey).startsWith("chat")
    ? "abandoned before engaging"
    : "abandoned before speaking";

  // NOT the bare word "engaged" (Markup #12). This count and the funnel's "Engaged"
  // stage are different, nested metrics — the funnel rung is the engaged step of the
  // booking-inquiry ladder, this is every conversation that reached the agent
  // (conversations − abandoned_before_speaking) — so on the same page one word marked
  // two different numbers (190 vs 191 on Verde). The action-based label says what this
  // figure is without borrowing the funnel stage's name, and mirrors the abandoned
  // split above: "spoke to" on Phone, "engaged with" on Chat.
  const engagedLabel = String(panelKey).startsWith("chat")
    ? "engaged with the agent"
    : "spoke to the agent";

  // The self-resolution line is the card's summary, so it heads the card as the
  // description (backend say[voice_workload] still wins when present). Omitted when
  // the rate is null — zero engaged has no resolution rate to state.
  /** A click handler for one figure, or undefined when the backend sent no drill. */
  const drillFor = (descriptor, label) =>
    onDrill && descriptor ? () => onDrill(descriptor, asTitle(label)) : undefined;

  const description = rate != null
    ? `${fmtNumber(rate, true)}% of engaged conversations resolved without a person.`
    : undefined;

  return (
    <PanelShell panelKey={panelKey} label="Workload" say={say} description={description} hasData={hasData} loading={loading}>
      <div data-testid="workload" className="flex flex-wrap gap-x-10 gap-y-3">
        <Stat testId="workload-engaged" value={engaged} label={engagedLabel} onClick={drillFor(wl.drill_engaged, engagedLabel)} />
        <Stat testId="workload-abandoned" value={abandoned} label={abandonedLabel} onClick={drillFor(wl.drill_abandoned, abandonedLabel)} />
        <Stat testId="workload-handled" value={handled} label={HANDLED_LABEL} onClick={drillFor(wl.drill_handled, HANDLED_LABEL)} />
      </div>
    </PanelShell>
  );
};

export default Workload;
